/**
 * openComputerForTask — ONE open for the Computer panel, whichever way the
 * person asked for it (ruling R-L8). The composer's Computer button and the
 * task-screen door's `open_computer` control must land on the same surface:
 * a task with browser context opens on Browser, everything else on Files.
 * Two copies of that rule drift the first time one of them is edited, so the
 * button and the door both call this instead of each doing the two calls.
 *
 * Reads the stores at CALL time (the sibling openBrowserForTask's idiom): the
 * affordance is a function of what is true when the person asks, and a control
 * that arrives from the server has no render of its own to read it from.
 */
import { useBrowserStore } from "@/stores/browserStore";
import { useComputerStore } from "@/stores/computerStore";
import { composerBrowserAffordance } from "@/lib/composer-browser-affordance";
import { getSidecarFrame } from "@/lib/frozen-frame-sidecar";

/** The surface a manual open lands on for this task — the button's own rule,
 *  derived from the same primitives it subscribes to. */
export function computerOpenTabForTask(taskId: string): "files" | "browser" {
  const computer = useComputerStore.getState();
  const browser = useBrowserStore.getState();
  return composerBrowserAffordance({
    panelOpen: computer.isOpen(taskId),
    viewState: browser.viewSnapshot?.state ?? null,
    agentContext: computer.agentContext(taskId),
    hasStoredFrame:
      Boolean(browser.frozenFrameByTask[taskId]) ||
      getSidecarFrame(taskId) !== null,
  }).openTab;
}

/** Open the Computer for this task on the surface the task actually uses. */
export function openComputerForTask(taskId: string): void {
  const computer = useComputerStore.getState();
  computer.open(taskId);
  computer.setActiveTab(taskId, computerOpenTabForTask(taskId));
}
